import React from "react";
import dynamic from "next/dynamic";
import Link from "next/link";

import getDispensariesV3 from "@/api/requests/consumerApi/getDispensariesV3";
import { StoreLocatorResultsResponse } from "@/api/requests/consumerApi/getStoreLocatorResults";
import { Action, Category } from "@/constants/events";
import { CountryCode } from "@/custom-types/CountryCode";
import { DispensaryV3 } from "@/custom-types/DispensariesV3";
import { Location } from "@/custom-types/Location";
import { Query } from "@/custom-types/Query";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import useWindowSize from "@/hooks/useWindowSize";
import { useSelector } from "@/redux/hooks";
import { getUserId } from "@/redux/selectors/user";
import { determineOrderMethod } from "@/utils/determineOrderMethod";
import { getRoundedDistance } from "@/utils/distance";
import { getMerchId } from "@/utils/getMerchId";
import formatImgixUrl from "@/utils/image/formatImgixUrl";
import isCanada from "@/utils/isCanada";
import { getLocationRoute } from "@/utils/locationUtils";

import Carousel from "@/components/botanic/Carousel";
import DispensaryCardPlaceholder from "@/components/botanic/DispensaryCard/DispensaryCardPlaceholder";
import PickupReadinessDispensaryCard from "@/components/PickupReadinessDispensaryCard";
import TrackImpression from "@/components/TrackImpression";

const NotFoundIcon = dynamic(() => import("@/components/Icons/not_found.svg"));

const CBDStoresCarousel = ({
  cbdStores,
  pageLocation,
}: {
  cbdStores: DispensaryV3[];
  pageLocation: Location;
}) => {
  const countryCode = useDomainCountryCode();
  const userId = useSelector(getUserId);

  const { publishEvent } = useEventTracker();

  const { isMobile } = useWindowSize();

  const title = `Shop ${isMobile ? "Hemp/" : ""}CBD stores near you`;

  const trackCardImpression = (slot: number, dispensary: DispensaryV3) => {
    const platinumPlacement = !!dispensary.merchandisedCampaigns?.find(
      (campaign) => campaign.unitName === "platinumplacement",
    );

    const merchandisingCampaignId = getMerchId(dispensary, "platinumplacement");

    publishEvent({
      action: Action.impression,
      category: Category.dispensaryCard,
      dispensaryId: dispensary.id,
      label: platinumPlacement
        ? `cbd carousel card platinum placement - ${slot + 1}`
        : `cbd carousel card - ${slot + 1}`,
      merchandisingCampaignId,
    });
  };

  const { city, radius, state, zip, formattedLocation } = pageLocation;

  const customDimensions = {
    searchCity: city,
    searchRadius: radius,
    searchState: state,
    searchZip: zip,
  };

  if (cbdStores.length === 0) {
    return null;
  }

  return (
    <TrackImpression
      category="Finder results"
      label="No locations nearby"
      className="finder__no-results mb-sm"
      data-testid="finder-no-results"
      customDimensions={customDimensions}
    >
      <div>
        <div className="border border-light-grey rounded flex flex-col items-center p-lg mt-lg">
          <NotFoundIcon width={60} />
          <h3 className="mt-sm font-bold">Looking for Hemp or CBD?</h3>
          <div className="mt-lg text-center">
            We don&apos;t see many THC stores near you, but we found these
            Hemp/CBD shops.
          </div>
        </div>
        <div className="mt-lg">
          <Carousel
            cardsPerSlide={4}
            preventCollapse
            title={title}
            subtitle={
              city && state && formattedLocation ? (
                <>
                  Showing you CBD stores near{" "}
                  <Link
                    className="font-bold cursor-pointer"
                    href={getLocationRoute("/cbd-stores", {
                      city,
                      state,
                    })}
                  >
                    {formattedLocation}
                  </Link>
                </>
              ) : null
            }
            seeMoreText="see all stores"
            seeMoreHref="/cbd-stores"
            id="cbd-stores-section"
            seeMoreOnClick={() => {
              publishEvent({
                action: Action.click,
                category: Category.carousel,
                label: "CBD stores carousel show all",
              });
            }}
          >
            {cbdStores.map(
              (store: DispensaryV3 | Record<string, never>, slot: number) => {
                if (store.name) {
                  const eventLabel = determineOrderMethod(
                    !!store.flags.includes("pickup"),
                    !!store.flags.includes("delivery"),
                    store.tier,
                    store.flags,
                  );
                  const platinumPlacement = store.merchandisedCampaigns?.find(
                    (campaign) => campaign.unitName === "platinumplacement",
                  );
                  const merchandisingCampaignId = platinumPlacement
                    ? platinumPlacement.id
                    : null;
                  return (
                    <TrackImpression
                      className="h-full"
                      trackingFunction={() =>
                        trackCardImpression(slot, store as DispensaryV3)
                      }
                      key={`dispensary-${slot}`}
                    >
                      <PickupReadinessDispensaryCard
                        key={store.name}
                        preserveHeight
                        currentStatus={store.currentStatuses?.store}
                        deliveryEnabled={!!store.flags.includes("delivery")}
                        distanceMi={store.distanceMi}
                        dispensaryId={store.id}
                        inStoreCartEnabled={store.inStoreCartEnabled}
                        isAd={store.isAd}
                        isNewDispensary={store.flags.includes("new_dispensary")}
                        logoUrl={store.logoUrl}
                        name={store.name}
                        pickupEnabled={!!store.flags.includes("pickup")}
                        preorderConfigured={
                          !!store.flags.includes("preorder_configured")
                        }
                        onClick={() => {
                          publishEvent({
                            action: Action.click,
                            category: Category.dispensaryCard,
                            dispensaryDistance: getRoundedDistance(
                              store.distanceMi,
                              countryCode,
                            ),
                            dispensaryId: store.id,
                            label: `slot #${slot + 1}: ${eventLabel}`,
                            merchandisingCampaignId:
                              merchandisingCampaignId || undefined,
                            userId,
                          });
                        }}
                        rating={store.starRating}
                        reviewCount={store.reviewCount}
                        scheduleType="store"
                        slug={store.slug}
                        tags={store.flags}
                        timeZone={store.timeZone}
                      />
                    </TrackImpression>
                  );
                } else {
                  return (
                    <DispensaryCardPlaceholder
                      id={`store-card-placeholder-${slot}`}
                      key={slot}
                      hideCTA
                    />
                  );
                }
              },
            )}
          </Carousel>
        </div>
      </div>
    </TrackImpression>
  );
};

type GetCbdStoresParams = {
  countryCode: CountryCode;
  filter: Query;
  pageLocation: Location;
  path: string;
  storeLocatorResults: StoreLocatorResultsResponse;
};

export const getCbdStores = async ({
  countryCode,
  filter,
  pageLocation,
  path,
  storeLocatorResults,
}: GetCbdStoresParams) => {
  const { coordinates, radius } = pageLocation;

  if (
    storeLocatorResults.metadata.pagination.totalCount > 5 ||
    isCanada(countryCode) ||
    path.includes("doctors") ||
    path.includes("cbd-stores") ||
    filter["ownership"] ||
    !coordinates?.lat ||
    !coordinates?.lon
  ) {
    return [];
  }

  const { lat, lon } = coordinates;

  const flags = ["cbd-store"];

  if (filter["delivery-enabled"] === "true") {
    flags.push("delivery");
  }

  return getDispensariesV3({
    flags,
    lat,
    lon,
    radius,
    take: 7,
  }).then(({ stores, sponsoredStores }) => {
    sponsoredStores.forEach((_store: DispensaryV3, i: number) => {
      sponsoredStores[i].isAd = true;
    });

    const dispensariesArray = sponsoredStores.concat(stores).splice(0, 7);

    dispensariesArray.forEach((dispensary: DispensaryV3, index: number) => {
      if (dispensary.logoUrl) {
        dispensariesArray[index].logoUrl = formatImgixUrl(dispensary.logoUrl);
      }
    });

    return dispensariesArray;
  });
};

export default CBDStoresCarousel;
